import React from "react";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { MultiValue } from "react-select";
import {
  useCreateProjectCategory,
  useDeleteProjectCategory,
  useProjectCategories,
  useUpdateProjectCategory,
} from "../../../api/useProjectCategories";

export const useSuspectedElementInput = (
  projectId: string,
  selectedEntries: { suspectedElements: { suspectedElement: { id: string; name: string } }[] }[],
) => {
  const suspectedElements = useProjectCategories(projectId, "SUSPECTED_ELEMENT");
  const createSuspectedElement = useCreateProjectCategory(projectId, "SUSPECTED_ELEMENT");
  const updateSuspectedElement = useUpdateProjectCategory(projectId, "SUSPECTED_ELEMENT");
  const deleteSuspectedElement = useDeleteProjectCategory(projectId, "SUSPECTED_ELEMENT");
  const [dirty, setDirty] = React.useState(false);
  const suspectedElementOptions = React.useMemo(
    () => buildOptions(suspectedElements.map(e => ({ label: e.name, value: e.id }))),
    [suspectedElements],
  );

  const suspectedElementDefaultValues = React.useMemo(
    () =>
      selectedEntries.flatMap((target: (typeof selectedEntries)[number]) =>
        target.suspectedElements?.reduce<BaseOption[]>(
          (acc, { suspectedElement: { name, id } }) => [...acc, { label: name, value: id }],
          [],
        ),
      ),
    [selectedEntries],
  );

  const [selectedSuspectedElements, setSelectedSuspectedElements] = React.useState<
    MultiValue<BaseOption>
  >(suspectedElementDefaultValues);

  const handleSuspectedElementChanged = React.useCallback(
    (values: MultiValue<BaseOption>) => {
      setSelectedSuspectedElements(values);
      setDirty(
        values.length !== suspectedElementDefaultValues.length ||
          values.some(
            ({ value }) => !suspectedElementDefaultValues.find(({ value: v }) => value === v),
          ),
      );
    },
    [suspectedElementDefaultValues],
  );

  const handleSuspectedElementCreated = React.useCallback(
    async (value: string) => {
      const { body: option } = await createSuspectedElement(value.replace(",", ""));
      setSelectedSuspectedElements(state => [...state, { value: option.id, label: option.name }]);
      setDirty(true);
    },
    [createSuspectedElement],
  );

  const handleSuspectedElementDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        await deleteSuspectedElement(option.value);

        if (selectedSuspectedElements.find(({ value: id }) => id === option.value)) {
          setDirty(true);
        }

        setSelectedSuspectedElements(state => state.filter(({ value: id }) => id !== option.value));
      }
    },
    [deleteSuspectedElement, selectedSuspectedElements],
  );

  const handleSuspectedElementEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        const escapedLabel = label.replace(",", "");
        await updateSuspectedElement(value, escapedLabel);

        if (selectedSuspectedElements.find(({ value: id }) => id === value)) {
          setDirty(true);
        }

        setSelectedSuspectedElements(state => {
          const i = state.findIndex(({ value: id }) => id === value);
          return state.map((el, j) => (i === j ? { label: escapedLabel, value } : el));
        });
      }
    },
    [updateSuspectedElement, selectedSuspectedElements],
  );

  const resetValues = React.useCallback(() => {
    setSelectedSuspectedElements(suspectedElementDefaultValues);
    setDirty(false);
  }, [suspectedElementDefaultValues]);

  return {
    suspectedElements,
    suspectedElementOptions,
    suspectedElementDefaultValues,
    selectedSuspectedElements,
    dirty,
    resetValues,
    handleSuspectedElementChanged,
    handleSuspectedElementCreated,
    handleSuspectedElementDeleted,
    handleSuspectedElementEdited,
  };
};
